/**
 * Checkpoint Manager
 * Manages code state checkpoints for safe undo/rewind functionality
 * Inspired by Claude Code's checkpoint system
 */

import { execSync } from 'child_process';
import fs from 'fs';
import path from 'path';
import {
  Checkpoint,
  CheckpointConfig,
  GitState,
  FileSnapshot,
  RewindOptions,
  RewindResult,
  DEFAULT_CHECKPOINT_CONFIG,
  DEFAULT_REWIND_OPTIONS,
} from './types';

export class CheckpointManager {
  private projectRoot: string;
  private checkpointsDir: string;
  private config: CheckpointConfig;
  private checkpoints: Checkpoint[] = [];
  private sessionId: string;

  constructor(projectRoot: string, sessionId: string, config?: Partial<CheckpointConfig>) {
    this.projectRoot = projectRoot;
    this.sessionId = sessionId;
    this.checkpointsDir = path.join(projectRoot, '.metis', 'checkpoints');
    this.config = { ...DEFAULT_CHECKPOINT_CONFIG, ...config };

    this.ensureCheckpointsDirectory();
    this.loadCheckpoints();
  }

  /**
   * Ensure checkpoints directory exists
   */
  private ensureCheckpointsDirectory(): void {
    if (!fs.existsSync(this.checkpointsDir)) {
      fs.mkdirSync(this.checkpointsDir, { recursive: true });
    }
  }

  /**
   * Load existing checkpoints from disk
   */
  private loadCheckpoints(): void {
    const indexPath = path.join(this.checkpointsDir, 'index.json');

    if (fs.existsSync(indexPath)) {
      try {
        const data = fs.readFileSync(indexPath, 'utf8');
        this.checkpoints = JSON.parse(data);
      } catch (error) {
        console.warn('Failed to load checkpoints index:', error);
        this.checkpoints = [];
      }
    }
  }

  /**
   * Save checkpoints index to disk
   */
  private saveCheckpointsIndex(): void {
    const indexPath = path.join(this.checkpointsDir, 'index.json');
    fs.writeFileSync(indexPath, JSON.stringify(this.checkpoints, null, 2));
  }

  /**
   * Check if checkpoints are enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Enable/disable checkpoints
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Check if a tool should trigger a checkpoint
   */
  shouldCreateCheckpoint(toolName: string): boolean {
    if (!this.config.enabled) return false;
    return this.config.triggerTools.includes(toolName);
  }

  /**
   * Get current git state
   */
  private getGitState(): GitState | null {
    try {
      // Check if we're in a git repo
      execSync('git rev-parse --git-dir', {
        cwd: this.projectRoot,
        stdio: 'pipe',
      });

      const branch = execSync('git rev-parse --abbrev-ref HEAD', {
        cwd: this.projectRoot,
        encoding: 'utf8',
      }).trim();

      const commit = execSync('git rev-parse HEAD', {
        cwd: this.projectRoot,
        encoding: 'utf8',
      }).trim();

      // Get staged files
      const stagedOutput = execSync('git diff --cached --name-only', {
        cwd: this.projectRoot,
        encoding: 'utf8',
      });
      const stagedFiles = stagedOutput.trim().split('\n').filter(Boolean);

      // Get modified files
      const modifiedOutput = execSync('git diff --name-only', {
        cwd: this.projectRoot,
        encoding: 'utf8',
      });
      const modifiedFiles = modifiedOutput.trim().split('\n').filter(Boolean);

      // Get untracked files
      const untrackedOutput = execSync('git ls-files --others --exclude-standard', {
        cwd: this.projectRoot,
        encoding: 'utf8',
      });
      const untrackedFiles = untrackedOutput.trim().split('\n').filter(Boolean);

      return {
        branch,
        commit,
        stagedFiles,
        modifiedFiles,
        untrackedFiles,
      };
    } catch (error) {
      // Not a git repo or git not available
      return null;
    }
  }

  /**
   * Create file snapshots for specified files
   */
  private createFileSnapshots(filePaths: string[]): FileSnapshot[] {
    const snapshots: FileSnapshot[] = [];

    for (const filePath of filePaths) {
      const absolutePath = path.isAbsolute(filePath)
        ? filePath
        : path.join(this.projectRoot, filePath);

      try {
        if (fs.existsSync(absolutePath)) {
          const content = fs.readFileSync(absolutePath, 'utf8');
          const stats = fs.statSync(absolutePath);

          snapshots.push({
            path: filePath,
            content,
            exists: true,
            permissions: stats.mode.toString(8),
          });
        } else {
          snapshots.push({
            path: filePath,
            content: '',
            exists: false,
          });
        }
      } catch (error) {
        console.warn(`Failed to snapshot file ${filePath}:`, error);
      }
    }

    return snapshots;
  }

  /**
   * Create a new checkpoint
   */
  createCheckpoint(
    description: string,
    triggerTool?: string,
    targetFiles?: string[],
    conversationIndex: number = 0
  ): Checkpoint | null {
    if (!this.config.enabled) return null;

    const gitState = this.getGitState();
    const id = `cp-${Date.now()}-${Math.random().toString(36).substring(7)}`;

    // Create file snapshots if configured and files specified
    let fileSnapshots: FileSnapshot[] | undefined;
    if (this.config.includeFileSnapshots && targetFiles && targetFiles.length > 0) {
      fileSnapshots = this.createFileSnapshots(targetFiles);
    }

    const checkpoint: Checkpoint = {
      id,
      timestamp: new Date().toISOString(),
      gitState: gitState || {
        branch: 'unknown',
        commit: 'unknown',
        stagedFiles: [],
        modifiedFiles: [],
        untrackedFiles: [],
      },
      conversationIndex,
      description,
      triggerTool,
      targetFiles,
      fileSnapshots,
      sessionId: this.sessionId,
    };

    // Add to beginning (most recent first)
    this.checkpoints.unshift(checkpoint);

    // Enforce max checkpoints
    if (this.checkpoints.length > this.config.maxCheckpoints) {
      const removed = this.checkpoints.splice(this.config.maxCheckpoints);
      // Clean up snapshot files for removed checkpoints
      for (const cp of removed) {
        this.cleanupCheckpointFiles(cp.id);
      }
    }

    // Save checkpoint data
    this.saveCheckpointData(checkpoint);
    this.saveCheckpointsIndex();

    return checkpoint;
  }

  /**
   * Save checkpoint data to disk
   */
  private saveCheckpointData(checkpoint: Checkpoint): void {
    const checkpointPath = path.join(this.checkpointsDir, `${checkpoint.id}.json`);
    fs.writeFileSync(checkpointPath, JSON.stringify(checkpoint, null, 2));
  }

  /**
   * Load a specific checkpoint
   */
  private loadCheckpointData(id: string): Checkpoint | null {
    const checkpointPath = path.join(this.checkpointsDir, `${id}.json`);

    if (fs.existsSync(checkpointPath)) {
      try {
        const data = fs.readFileSync(checkpointPath, 'utf8');
        return JSON.parse(data);
      } catch (error) {
        return null;
      }
    }

    return null;
  }

  /**
   * Clean up checkpoint files
   */
  private cleanupCheckpointFiles(id: string): void {
    const checkpointPath = path.join(this.checkpointsDir, `${id}.json`);

    if (fs.existsSync(checkpointPath)) {
      fs.unlinkSync(checkpointPath);
    }
  }

  /**
   * Get all checkpoints (most recent first)
   */
  getCheckpoints(): Checkpoint[] {
    return [...this.checkpoints];
  }

  /**
   * Get the most recent checkpoint
   */
  getLatestCheckpoint(): Checkpoint | null {
    return this.checkpoints[0] || null;
  }

  /**
   * Get checkpoint by ID
   */
  getCheckpoint(id: string): Checkpoint | null {
    const index = this.checkpoints.find(cp => cp.id === id);
    if (index) {
      // Load full data including file snapshots
      return this.loadCheckpointData(id) || index;
    }
    return null;
  }

  /**
   * Get checkpoints for current session
   */
  getSessionCheckpoints(): Checkpoint[] {
    return this.checkpoints.filter(cp => cp.sessionId === this.sessionId);
  }

  /**
   * Rewind to a specific checkpoint
   */
  async rewind(
    checkpointId: string,
    options: Partial<RewindOptions> = {}
  ): Promise<RewindResult> {
    const opts = { ...DEFAULT_REWIND_OPTIONS, ...options };

    const checkpoint = this.getCheckpoint(checkpointId);
    if (!checkpoint) {
      return {
        success: false,
        checkpoint: {} as Checkpoint,
        restoredGit: false,
        restoredConversation: false,
        restoredFiles: [],
        error: `Checkpoint not found: ${checkpointId}`,
      };
    }

    // Create backup checkpoint before rewinding
    let backupCheckpointId: string | undefined;
    if (opts.createBackup) {
      const backup = this.createCheckpoint(
        `Backup before rewind to ${checkpointId}`,
        'rewind',
        checkpoint.targetFiles,
        checkpoint.conversationIndex
      );
      if (backup) {
        backupCheckpointId = backup.id;
      }
    }

    const result: RewindResult = {
      success: true,
      checkpoint,
      restoredGit: false,
      restoredConversation: false,
      restoredFiles: [],
      backupCheckpointId,
    };

    try {
      // Restore git state
      if (opts.restoreGit && checkpoint.gitState) {
        const gitRestored = await this.restoreGitState(checkpoint.gitState);
        result.restoredGit = gitRestored;
      }

      // Restore file snapshots
      if (opts.restoreFiles && checkpoint.fileSnapshots) {
        const restoredFiles = this.restoreFileSnapshots(checkpoint.fileSnapshots);
        result.restoredFiles = restoredFiles;
      }

      // Mark checkpoint as rewound
      checkpoint.rewoundAt = new Date().toISOString();
      this.saveCheckpointData(checkpoint);

      return result;
    } catch (error) {
      result.success = false;
      result.error = error instanceof Error ? error.message : String(error);
      return result;
    }
  }

  /**
   * Restore git state
   */
  private async restoreGitState(gitState: GitState): Promise<boolean> {
    try {
      // Stash current changes
      execSync('git stash push -m "metis-rewind-backup"', {
        cwd: this.projectRoot,
        stdio: 'pipe',
      });

      // Checkout the branch
      if (gitState.branch !== 'HEAD') {
        execSync(`git checkout ${gitState.branch}`, {
          cwd: this.projectRoot,
          stdio: 'pipe',
        });
      }

      // Reset to the commit
      execSync(`git reset --hard ${gitState.commit}`, {
        cwd: this.projectRoot,
        stdio: 'pipe',
      });

      return true;
    } catch (error) {
      console.warn('Failed to restore git state:', error);
      return false;
    }
  }

  /**
   * Restore file snapshots
   */
  private restoreFileSnapshots(snapshots: FileSnapshot[]): string[] {
    const restored: string[] = [];

    for (const snapshot of snapshots) {
      try {
        const absolutePath = path.isAbsolute(snapshot.path)
          ? snapshot.path
          : path.join(this.projectRoot, snapshot.path);

        if (snapshot.exists) {
          // Ensure directory exists
          const dir = path.dirname(absolutePath);
          if (!fs.existsSync(dir)) {
            fs.mkdirSync(dir, { recursive: true });
          }

          // Restore file content
          fs.writeFileSync(absolutePath, snapshot.content, 'utf8');

          // Restore permissions if available
          if (snapshot.permissions) {
            fs.chmodSync(absolutePath, parseInt(snapshot.permissions, 8));
          }
        } else {
          // File didn't exist - delete it if it exists now
          if (fs.existsSync(absolutePath)) {
            fs.unlinkSync(absolutePath);
          }
        }

        restored.push(snapshot.path);
      } catch (error) {
        console.warn(`Failed to restore file ${snapshot.path}:`, error);
      }
    }

    return restored;
  }

  /**
   * Quick rewind to the previous checkpoint (Esc+Esc functionality)
   */
  async quickRewind(): Promise<RewindResult> {
    const latest = this.getLatestCheckpoint();
    if (!latest) {
      return {
        success: false,
        checkpoint: {} as Checkpoint,
        restoredGit: false,
        restoredConversation: false,
        restoredFiles: [],
        error: 'No checkpoints available',
      };
    }

    return this.rewind(latest.id, {
      restoreGit: true,
      restoreFiles: true,
      restoreConversation: false, // Quick rewind doesn't restore conversation
      createBackup: true,
    });
  }

  /**
   * Delete a checkpoint
   */
  deleteCheckpoint(id: string): boolean {
    const index = this.checkpoints.findIndex(cp => cp.id === id);
    if (index !== -1) {
      this.checkpoints.splice(index, 1);
      this.cleanupCheckpointFiles(id);
      this.saveCheckpointsIndex();
      return true;
    }
    return false;
  }

  /**
   * Clear all checkpoints
   */
  clearCheckpoints(): void {
    for (const cp of this.checkpoints) {
      this.cleanupCheckpointFiles(cp.id);
    }
    this.checkpoints = [];
    this.saveCheckpointsIndex();
  }

  /**
   * Auto-cleanup old checkpoints
   */
  autoCleanup(): number {
    if (!this.config.autoCleanupHours) return 0;

    const cutoff = new Date();
    cutoff.setHours(cutoff.getHours() - this.config.autoCleanupHours);

    let removed = 0;
    this.checkpoints = this.checkpoints.filter(cp => {
      const cpDate = new Date(cp.timestamp);
      if (cpDate < cutoff) {
        this.cleanupCheckpointFiles(cp.id);
        removed++;
        return false;
      }
      return true;
    });

    if (removed > 0) {
      this.saveCheckpointsIndex();
    }

    return removed;
  }

  /**
   * Get checkpoint summary for display
   */
  getCheckpointsSummary(): {
    total: number;
    sessionCount: number;
    oldestTimestamp?: string;
    newestTimestamp?: string;
  } {
    const sessionCheckpoints = this.getSessionCheckpoints();

    return {
      total: this.checkpoints.length,
      sessionCount: sessionCheckpoints.length,
      oldestTimestamp: this.checkpoints[this.checkpoints.length - 1]?.timestamp,
      newestTimestamp: this.checkpoints[0]?.timestamp,
    };
  }
}

// Global checkpoint manager instance
let globalCheckpointManager: CheckpointManager | null = null;

export function getCheckpointManager(
  projectRoot?: string,
  sessionId?: string
): CheckpointManager {
  if (!globalCheckpointManager) {
    globalCheckpointManager = new CheckpointManager(
      projectRoot || process.cwd(),
      sessionId || `session-${Date.now()}`
    );
  }
  return globalCheckpointManager;
}

export function resetCheckpointManager(): void {
  globalCheckpointManager = null;
}
